import { getItemFromLocalStorage } from 'lib/helper';
import history from '../../../history.js';

export const GITHUB_URL = process.env.REACT_APP_GIT_URL;
export const BITBUCKET_URL = process.env.REACT_APP_BITBUCKET_URL;
export const GITLAB_URL = process.env.REACT_APP_GITLAB_URL;
export const AZURE_URL = process.env.REACT_APP_AZURE_URL;

export enum HostType {
  Azure = 'azure',
  Github = 'github',
  Gitlab = 'gitlab',
  Bitbucket = 'bitbucket',
}

export enum RespositoryStatusOptions {
  Connected = 'Connected',
  Scheduled = 'Scheduled',
  Scanning = 'Scanning',
  PartiallyScanned = 'Partially Scanned',
  ScanCompleted = 'Scan Completed',
  AnalyzingResults = 'Analyzing Results',
  Syncing = 'Syncing',
  AwaitingSync = 'Awaiting Sync',
  SyncCompleted = 'Sync Completed',
  ScanError = 'Scan Error',
  TimeoutDelay = 'Timeout/Delay',
  Disconnected = 'Disconnected',
  Deactivated = 'Deactivated',
}

export enum RepositoryFilterOptions {
  Active = 'active',
  Inactive = 'inactive',
  Error = 'error',
  Disconnected = 'disconnected',
  Archived = 'archived',
}

import callApi from 'lib/callApi';

export async function fetchRepositoryDetail(id, companyId) {
  let url = `sast/repositories/${id}/`;
  const params = {
    company_id: companyId,
  };
  const data = await callApi(url, 'GET', params)
    .then((res) => {
      if (res.data) {
        return res.data;
      }
    })
    .catch((err) => {
      throw err;
    });
  return data;
}

export const processJsonData = (json: any) => {
  if (!json) return;

  // process fullname
  let fullname: any = null;
  if (
    json['added_by'] &&
    json['added_by']['first_name'] &&
    json['added_by']['last_name']
  ) {
    fullname = `${json['added_by']['first_name']} ${json['added_by']['last_name']}`;
  }

  let accountFullname: any = null;
  if (
    json['account'] &&
    json['account']['user'] &&
    json['account']['user']['first_name'] &&
    json['account']['user']['last_name']
  ) {
    accountFullname = `${json['account']['user']['first_name']} ${json['account']['user']['last_name']}`;
  }

  let deactivatedByFullName: any = null;
  if (
    json['deactivated_by'] &&
    json['deactivated_by']['first_name'] &&
    json['deactivated_by']['last_name']
  ) {
    deactivatedByFullName = `${json['deactivated_by']['first_name']} ${json['deactivated_by']['last_name']}`;
  }

  let reactivatedByFullName: any = null;
  if (
    json['reactivated_by'] &&
    json['reactivated_by']['first_name'] &&
    json['reactivated_by']['last_name']
  ) {
    reactivatedByFullName = `${json['reactivated_by']['first_name']} ${json['reactivated_by']['last_name']}`;
  }

  let supportedLanguages: any = null;
  if (json['languages'] && json['languages']['supported_languages']) {
    supportedLanguages = `${json['languages']['supported_languages'].join(
      ', ',
    )}`;
  }

  let unsupportedLanguages: any = null;
  if (json['languages'] && json['languages']['unsupported_languages']) {
    unsupportedLanguages = `${json['languages']['unsupported_languages'].join(
      ', ',
    )}`;
  }

  const processedJson = {
    ...json,
    account: {
      ...json['account'],
      user: {
        ...json['account']['user'],
        fullname: accountFullname,
      },
    },
    added_by: {
      ...json['added_by'],
      fullname: fullname,
    },
    deactivated_by: {
      ...json['deactivated_by'],
      fullname: deactivatedByFullName,
    },
    reactivated_by: {
      ...json['reactivated_by'],
      fullname: reactivatedByFullName,
    },
    languages: {
      ...json['languages'],
      supported_languages: supportedLanguages,
      unsupported_languages: unsupportedLanguages,
    },
  };

  return processedJson;
};

export function getExportCsvUrlParams(
  companyId: number,
  ordering: string,
  accountIds: number[],
  hostTypes: string[],
  stateFilters: string[],
  checkedHostIds: number[],
) {
  if (companyId === -1) return '';
  const params = {
    company_id: companyId,
    host_types: hostTypes.join(','),
    account_ids: accountIds.join(','),
    state_filters: stateFilters.join(','),
  };
  if (checkedHostIds?.length > 0) {
    params['host_id_list'] = checkedHostIds;
  }
  if (ordering && ordering !== 'name') {
    params['ordering'] = `${ordering},name`;
  }

  return params;
}

export const BASE_URL: string = getItemFromLocalStorage('approle') || '';

export const getOAuthUrl = (
  hostType: HostType,
  connectionType: 'new' | 'revive',
  accountId: number | undefined = undefined,
) => {
  type UrlMap = {
    [key in HostType]: string | undefined;
  };

  const urlMap: UrlMap = {
    [HostType.Azure]: AZURE_URL,
    [HostType.Github]: GITHUB_URL,
    [HostType.Gitlab]: GITLAB_URL,
    [HostType.Bitbucket]: BITBUCKET_URL,
  };

  const oAuthUrl = urlMap[hostType];

  if (oAuthUrl) {
    return updateAuthUrlStateParam(oAuthUrl, connectionType, accountId);
  } else {
    console.error(`${hostType} oAuth URL is not defined.`);
    return '';
  }
};

export interface State {
  hostType: string;
  mode: string;
  accountId?: number;
}

export const updateAuthUrlStateParam = (
  url: string | undefined,
  mode: string,
  accountId: number | undefined = undefined,
): string | undefined => {
  if (!url) return url;

  const urlObject = new URL(url);
  const searchParams = new URLSearchParams(urlObject.search);

  const currentState = searchParams.get('state');
  let hostTypeValue = currentState;
  switch (currentState) {
    case 'githubapp':
      hostTypeValue = 'github';
      break;
    case 'gitlabapp':
      hostTypeValue = 'gitlab';
      break;
    case 'azureapp':
      hostTypeValue = 'azure';
      break;
    case 'bitbucketapp':
      hostTypeValue = 'bitbucket';
      break;
    default:
      hostTypeValue = currentState;
      break;
  }
  if (hostTypeValue) {
    const newState: State = {
      hostType: hostTypeValue,
      mode: mode,
    };

    if (accountId) {
      newState.accountId = accountId;
    }
    searchParams.set('state', encodeURIComponent(JSON.stringify(newState)));
  }

  urlObject.search = searchParams.toString();
  return urlObject.toString();
};

export const getOAuthStateData = ():
  | { code: string; state: State }
  | undefined => {
  const searchParams = new URLSearchParams(window.location.search);
  const stateParam = searchParams.get('state');
  const code = searchParams.get('code');

  if (stateParam && code) {
    const decodedData = decodeURIComponent(stateParam);
    return { code: code, state: JSON.parse(decodedData) };
  }

  return undefined;
};

export const handleSastOAuthReceived = (): boolean => {
  const searchParams = new URLSearchParams(window.location.search);
  const stateParam = searchParams.get('state');
  const code = searchParams.get('code');

  if (stateParam) {
    const decodedState = decodeURIComponent(stateParam);

    try {
      const stateObject = JSON.parse(decodedState);
      //
      if (stateObject && stateObject.hostType && stateObject.mode) {
        history.push(
          `/${BASE_URL}/sast?code=${code}&state=${stateParam}&t=new`,
        );
        return true;
      } else {
        console.error('Invalid state, expected params missing');
      }
    } catch (error) {
      console.error('Error parsing JSON state:', error);
    }
  } else {
    console.info("No 'state' parameter found in the URL.");
  }
  return false;
};

export const getHostLabel = (type: HostType) => {
  switch (type) {
    case 'azure':
      return 'Azure DevOps';
    case 'github':
      return 'Github';
    case 'gitlab':
      return 'Gitlab';
    case 'bitbucket':
      return 'Bitbucket';
    default:
      return '';
  }
};

export function getEnumKeyByEnumValue<T extends { [index: string]: string }>(
  myEnum: T,
  enumValue: string,
): keyof T {
  const keys = Object.keys(myEnum).filter((x) => myEnum[x] === enumValue);
  return keys[0];
}
